import { t } from "@lingui/macro";
import {
  Browsers,
  CaretDown,
  CaretRight,
  Check,
  CircleNotch,
  Copy,
  DownloadSimple,
  Fire,
  House,
  MagicWand,
  PaintBrushHousehold,
  Robot,
  ShieldWarning,
  Sparkle,
  Trash,
  Translate,
  Wrench,
} from "@phosphor-icons/react";
import { cn } from "@stylingcv/utils";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAuthStore } from "@/client/stores/auth";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";
import { useDeleteResume } from "@/client/services/resume/delete";
import { useImportResume } from "@/client/services/resume/import";
import { TranslateResumeModal } from "./translate-resume-modal";

type BuilderTopbarProps = {
  onDownload?: () => void;
  onTabChange: (tab: "fill-in" | "design" | "analysis") => void;
};

type DropdownItem =
  | {
      type: "item";
      label: string;
      description: string;
      icon: React.ReactNode;
      onClick: () => void;
      danger?: boolean;
      badge?: { kind: "ai" } | { kind: "kbd"; label: string };
    }
  | { type: "separator" };

export const BuilderTopbar = ({ onDownload, onTabChange }: BuilderTopbarProps) => {
  const isSaving = useBuilderStore((state) => state.isSaving);
  const setShowCoverLetterModal = useBuilderStore((state) => state.setShowCoverLetterModal);
  const title = useResumeStore((state) => state.resume.title);
  const resume = useResumeStore((state) => state.resume);
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const navigate = useNavigate();
  const { deleteResume } = useDeleteResume();
  const { importResume } = useImportResume();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showTranslateModal, setShowTranslateModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  const handleDuplicate = async () => {
    setDropdownOpen(false);
    await importResume({
      title: `${resume.title} (Copy)`,
      slug: `${resume.slug}-copy`,
      data: resume.data,
    });
  };

  const handleDelete = async () => {
    if (!resume.id) return;
    setDropdownOpen(false);
    if (!window.confirm(t`Are you sure you want to delete this resume? This action cannot be undone.`)) return;
    await deleteResume(resume.id);
    navigate("/dashboard/resumes");
  };

  const dropdownItems: DropdownItem[] = [
    {
      type: "item",
      label: t`Change resume design`,
      description: t`Colors, spacing & typography`,
      icon: <PaintBrushHousehold size={17} weight="duotone" />,
      onClick: () => { onTabChange("design"); setDropdownOpen(false); },
    },
    {
      type: "item",
      label: t`AI Suggestions`,
      description: t`Smart improvements for this resume`,
      icon: <Sparkle size={17} weight="duotone" />,
      onClick: () => { onTabChange("analysis"); setDropdownOpen(false); },
      badge: { kind: "ai" },
    },
    {
      type: "item",
      label: t`Templates`,
      description: t`Browse all resume layouts`,
      icon: <Browsers size={17} weight="duotone" />,
      onClick: () => { onTabChange("design"); setDropdownOpen(false); },
      badge: { kind: "kbd", label: "⌘T" },
    },
    {
      type: "item",
      label: t`Copy to other language`,
      description: t`Choose from 50+ languages`,
      icon: <Translate size={17} weight="duotone" />,
      onClick: () => { setShowTranslateModal(true); setDropdownOpen(false); },
      badge: { kind: "ai" },
    },
    {
      type: "item",
      label: t`ATS Check`,
      description: t`Score your resume for job boards`,
      icon: <Robot size={17} weight="duotone" />,
      onClick: () => { onTabChange("analysis"); setDropdownOpen(false); },
      badge: { kind: "ai" },
    },
    {
      type: "item",
      label: t`Generate cover letter`,
      description: t`Match this resume to a job`,
      icon: <MagicWand size={17} weight="duotone" />,
      onClick: () => { setShowCoverLetterModal(true); setDropdownOpen(false); },
      badge: { kind: "ai" },
    },
    {
      type: "item",
      label: t`Stylion — Resume Roast`,
      description: t`Get a royal AI critique of your resume`,
      icon: <Fire size={17} weight="duotone" />,
      onClick: () => { navigate("/stylion"); setDropdownOpen(false); },
      badge: { kind: "ai" },
    },
    { type: "separator" },
    {
      type: "item",
      label: t`Duplicate`,
      description: t`Make a copy of this resume`,
      icon: <Copy size={17} weight="duotone" />,
      onClick: handleDuplicate,
      badge: { kind: "kbd", label: "⌘D" },
    },
    {
      type: "item",
      label: t`Delete resume`,
      description: t`Permanently remove this resume`,
      icon: <Trash size={17} weight="duotone" />,
      danger: true,
      onClick: handleDelete,
    },
  ];

  return (
    <>
    <TranslateResumeModal isOpen={showTranslateModal} onClose={() => setShowTranslateModal(false)} />
    <div className="stylion-app-shell flex h-[64px] min-h-[64px] w-full shrink-0 items-center border-b">
      {/* Logo zone — 248px matching section rail */}
      <Link
        to="/dashboard/resumes"
        aria-label={t`Go to dashboard`}
        className="flex h-full w-[248px] shrink-0 items-center border-r border-black/5 px-6 transition-opacity hover:opacity-80"
      >
        <img src="/logo/stylingcv-logo-light.svg" alt="Stylion" className="h-[26px] w-auto" />
      </Link>

      {/* Content zone */}
      <div className="relative flex flex-1 items-center gap-[10px] pl-6">
        <Link
          to="/dashboard/resumes"
          className="flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:opacity-80"
        >
          <House size={16} />
        </Link>

        <CaretRight size={14} className="text-[var(--fg-4,#a3a3a3)] rtl:rotate-180" />

        <span className="max-w-[260px] truncate text-sm font-semibold text-[var(--stylion-ink)]">
          {title || t`Untitled Resume`}
        </span>

        <div className="w-3" />

        {/* Resume tools dropdown */}
        <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setDropdownOpen((prev) => !prev)}
            className="flex items-center gap-1.5 rounded-full border border-black/10 bg-black/[0.04] px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-black/[0.06]"
            aria-haspopup="true"
            aria-expanded={dropdownOpen}
          >
            <Wrench size={13} />
            <span>{t`Resume tools`}</span>
            <CaretDown
              size={11}
              className={cn("text-gray-400 transition-transform", dropdownOpen && "rotate-180")}
            />
          </button>

          {dropdownOpen && (
            <div className="glass-strong absolute left-0 top-full z-50 mt-2 w-[320px] overflow-hidden rounded-2xl">
              {/* Header */}
              <div className="px-4 pt-4 pb-3 border-b border-gray-100">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{t`Resume Tools`}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{t`Actions on the whole document`}</p>
              </div>

              {/* Items */}
              <div className="p-2">
                {dropdownItems.map((item, idx) => {
                  if (item.type === "separator") {
                    return <div key={idx} className="my-1.5 h-px bg-gray-100 mx-2" />;
                  }
                  return (
                    <button
                      key={idx}
                      onClick={item.onClick}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors group",
                        item.danger ? "hover:bg-red-50" : "hover:bg-gray-50",
                      )}
                    >
                      {/* Icon box */}
                      <div className={cn(
                        "flex size-9 shrink-0 items-center justify-center rounded-xl transition-colors",
                        item.danger
                          ? "bg-red-50 text-red-400 group-hover:bg-red-100"
                          : "bg-gray-100 text-gray-500 group-hover:bg-gray-200",
                      )}>
                        {item.icon}
                      </div>

                      {/* Label + description */}
                      <div className="flex-1 min-w-0">
                        <p className={cn(
                          "text-[13px] font-semibold leading-tight",
                          item.danger ? "text-red-500" : "text-[var(--stylion-ink)]",
                        )}>
                          {item.label}
                        </p>
                        <p className="text-[11px] text-gray-400 leading-tight mt-0.5 truncate">
                          {item.description}
                        </p>
                      </div>

                      {/* Badge */}
                      {item.badge && item.badge.kind === "ai" && (
                        <span className="shrink-0 rounded-md bg-orange-50 border border-orange-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-orange-500">
                          AI
                        </span>
                      )}
                      {item.badge && item.badge.kind === "kbd" && (
                        <kbd className="shrink-0 rounded-md border border-gray-200 bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 font-mono">
                          {item.badge.label}
                        </kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-2 pr-6">
        {!isLoggedIn ? (
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700">
            <ShieldWarning size={14} weight="fill" className="text-amber-500" />
            <span>{t`Don't lose your work`}</span>
            <span className="rounded bg-amber-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
              {t`Save`}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-[13px] font-medium text-[var(--fg-3,#737373)]">
            {isSaving ? (
              <>
                <CircleNotch size={14} className="animate-spin" />
                <span>{t`Saving...`}</span>
              </>
            ) : (
              <>
                <Check size={14} className="text-[var(--scv-success,#22c55e)]" weight="bold" />
                <span>{t`Auto-saved`}</span>
              </>
            )}
          </div>
        )}

        <div className="h-4 w-px bg-gray-200" />

        <button
          onClick={onDownload}
          className="flex items-center gap-2 rounded-full bg-gradient-to-r from-primary to-accent px-4 py-2 text-[13px] font-bold text-primary-foreground shadow-[var(--glow-primary)] transition-opacity hover:opacity-90"
        >
          <DownloadSimple size={14} weight="bold" />
          <span>{t`Download PDF`}</span>
        </button>
      </div>
    </div>
    </>
  );
};
